/**
 * The Übersicht "next step" card (COL-533).
 *
 * The design replaces V1's wall of status cards with one card saying the
 * single most important thing the tenant has to do. That ranking already
 * exists — `computeTenantNextActions` — so this derives the facts and reuses
 * it rather than inventing a second ordering that could disagree.
 *
 * All money facts come from `useTenantMoneyFacts`, shared with the shell's
 * journey derivation. They were derived separately before, which let a fix
 * land in one place and not the other.
 */

import { ArrowRight, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { parseDateOnly } from "@/lib/roomAvailability";
import { getImageUrl } from "@/lib/imageUrl";
import { firstImageUrl } from "@/lib/roomImages";
import {
  HERO_JOURNEY_STEPS,
  heroJourneyIndex,
  showHeroJourney,
  stayProgress,
} from "@/lib/tenant-overview-hero";
import { useNowTick } from "@/hooks/useNowTick";
import { useReferralRewardStalled } from "@/hooks/useReferralRewardStalled";
import { useTenantIssueHistory } from "@/hooks/useTenantIssueHistory";
import { useTenantMoneyFacts } from "@/hooks/useTenantMoneyFacts";
import { translateDictionary } from "@/lib/i18n/dictionary";
import { baseLanguageFor, localeFor } from "@/lib/i18n/languages";
import { useProtocolActionNeeded } from "@/hooks/useProtocolActionNeeded";
import { useActiveBooking, useProtocolsForBooking } from "@/hooks/useProtocol";
import { getTenantDashboardV2Labels } from "@/lib/tenant-dashboard-v2-copy";
import { remainingRentCents } from "@/lib/rent";
import { isContractAwaitingTenantSignature } from "@/lib/tenant-payment-scope";
import { computeTenantNextActions } from "@/lib/tenant-next-action";
import { formatTenantDate } from "@/lib/date-format";

type Props = {
  booking: any;
  /** Opens the tab the action belongs to. */
  onOpen: (tab: "room" | "help" | "referrals") => void;
  /**
   * The tabs this stage actually offers.
   *
   * An action whose tab is locked is not an action: a stalled referral reward
   * before move-in offered a button to Referrals, which the stage hides, so
   * the click did nothing. Such actions are dropped rather than rendered dead.
   */
  availableTabs: readonly string[];
  /** Drives the journey strip and whether the quick-action row shows. */
  stage?: string | null;
  /**
   * The greeting, rendered INSIDE the hero.
   *
   * The design puts it in the hero's left column with the date line above it,
   * not floating over the whole page — so the shell hands it down rather than
   * printing its own h1 above every tab.
   */
  greeting?: string;
};

export default function TenantOverview({
  booking,
  onOpen,
  availableTabs,
  stage = null,
  greeting = "",
}: Props) {
  const { tDict, language: activeLanguage } = useLanguage();
  const language = baseLanguageFor(activeLanguage);
  const d = translateDictionary(
    "tenant.dashboardV2",
    getTenantDashboardV2Labels(language),
    (key, literal) => tDict(key, literal ?? "", activeLanguage)
  );

  // Same minute tick the greeting uses, so the date line and the stay
  // progress cross midnight on a dashboard left open.
  const now = useNowTick();
  const money = useTenantMoneyFacts(booking);
  /**
   * The SAME hook V1's tab-strip pip uses, not a hand-rolled call to
   * `computeProtocolActionNeeded`.
   *
   * It resolves its booking through `useActiveBooking` — latest start date,
   * falling back to `start_date` — which is also what `ProtocolFlow` on the
   * room page runs. Deriving this from `useTenantBooking` instead (newest
   * created, raw `move_in_date`) let the two pick different bookings, so the
   * overview could omit a move-in task for the very protocol the room page
   * was showing. It also brings the `now` tick and the still-loading guard,
   * so a window boundary cannot go stale and an unloaded list cannot read as
   * "not started" for a tenant who has already submitted.
   */
  const protocolAction = useProtocolActionNeeded();
  // That hook returns null in three different situations: no action needed,
  // still loading, and its own booking query failed. Only the first means
  // "nothing to do", so the booking it resolved is checked directly — without
  // this, a tenant whose move-in protocol is due reads "All done" for as long
  // as the protocol queries take, and indefinitely if they fail.
  const { data: protocolBooking, isPending: bookingPending } =
    useActiveBooking();
  // The LIST matters as much as the booking. Once the booking resolves but the
  // protocols are still loading, `useProtocolActionNeeded` deliberately returns
  // null — indistinguishable from "nothing needed" — so a tenant with a
  // handover due read "All done" for that whole window.
  const { isPending: protocolsPending, isError: protocolsFailed } =
    useProtocolsForBooking(protocolBooking?.id ?? null);
  /*
    The list's pending flag only counts once a booking EXISTS.

    `useProtocolsForBooking` is `enabled: !!bookingId`, and in TanStack v5 a
    disabled query with no data sits at `status: "pending"` forever. So with a
    null booking — no active tenancy, or a failed booking query — this was
    permanently true: the overview showed "Loading" indefinitely, never reached
    the error state, and hid the signature, deposit and overdue actions it had
    been showing before. A disabled query is not a loading one.
  */
  const protocolPending =
    bookingPending || (!!protocolBooking && protocolsPending);
  /*
    A FAILED list is not evidence either.

    On failure TanStack reports `isPending: false` and hands back the empty
    default, which `computeProtocolActionNeeded` reads as "not started" — so a
    tenant who has already submitted could be asked to do it again, or a real
    outstanding handover could vanish behind "All done". Absent data and wrong
    data get the same treatment: say nothing.
  */
  const protocolStateUnknown =
    protocolPending || protocolsFailed || !protocolBooking;
  const referralRewardStalled = useReferralRewardStalled();
  /*
    Open requests only. A closed one is history, and a tile that counted it
    would keep saying "3 open" after everything was resolved.

    Called here, BEFORE the loading/error returns below -- a hook called
    after an early return changes which hooks run between renders (rules of
    hooks), and this one used to sit past both of them.
  */
  const {
    data: openIssues = [],
    isPending: issuesPending,
    isError: issuesFailed,
  } = useTenantIssueHistory("open");

  // Missing evidence is not "nothing to do". Rendering "All done" while the
  // ledger or its proofs are still resolving — or after they failed — would
  // hide a real outstanding signature or an overdue month, indefinitely on
  // error.
  if (money.loading || protocolPending) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground">{d.loading}</p>
      </Card>
    );
  }
  if (money.error) {
    return (
      <Card className="p-6">
        <p className="text-destructive">{d.loadFailed}</p>
      </Card>
    );
  }

  const actions = computeTenantNextActions({
    contractAwaitingSignature: isContractAwaitingTenantSignature(booking),
    // Owed AND the contract has reached a state where asking is fair —
    // `isContractSignedForJourney`, the stage ladder's own deposit gate, so an
    // applicant is never told to pay before their contract exists.
    depositDue: money.depositActionable,
    rentOverdue: money.rentOverdue,
    /*
      Suppressed while the protocol state is unknown, not just reported.

      `useProtocolActionNeeded` holds its answer back while the list is
      PENDING but not when it FAILED: on failure TanStack reports
      `isPending: false` and hands back the empty default, which
      `computeProtocolActionNeeded` reads as "not started" and turns into a
      move-in/move-out action. That action makes `top` non-null, so the
      `protocolStateUnknown` branch below — which exists for exactly this —
      never runs, and a tenant who already submitted is told to do it again.
      The guard has to sit on both exits, not only on the "nothing to do" one.
    */
    protocolKind: protocolStateUnknown ? null : (protocolAction?.kind ?? null),
    /*
      The keys are still out after the walkthrough (Neel, 23 Oct).

      `key_return_pending` is the stage ladder's own answer to "move-out
      protocol submitted, `key_returned_at` not set" — derived from the
      booking we already have, so this needs no query of its own. Reading the
      stage rather than the raw columns keeps this and the journey strip
      saying the same thing.
    */
    keyReturnPending: stage === "key_return_pending",
    /*
      Move-in still ahead, for a tenancy that is otherwise in order.

      Strictly in the FUTURE: a tenant who already moved in has a move-in date
      too, and a past one must not produce an upcoming-move-in step. Ranked
      last, so this only ever reaches the card when nothing else is open --
      which is exactly the state that used to render "all done" to somebody
      days away from moving in.
    */
    moveInUpcoming: (() => {
      const iso = booking?.move_in_date ?? booking?.start_date ?? null;
      const parsed = iso ? parseDateOnly(String(iso)) : null;
      if (!parsed || Number.isNaN(parsed.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return parsed.getTime() > today.getTime();
    })(),
    // The one fact still unwired. V1 derives it by scanning issue_comments
    // against per-issue local read marks (~80 lines); duplicating that would
    // risk the two disagreeing. It is also the only fact that is a
    // notification rather than a task with a consequence.
    supportUnread: 0,
    referralRewardStalled,
  });

  const tabFor = (t: string): "room" | "help" | "referrals" =>
    t === "maintenance" ? "help" : t === "referrals" ? "referrals" : "room";

  const top =
    actions.find((a) => availableTabs.includes(tabFor(a.tab))) ?? null;

  /*
    The hero, ported from Vivi's export (COL-533).

    Everything here comes off the booking's own dates, the room's existing
    photo and the journey stage — no new columns. The two-column grid is the
    design's: text and next-step card on the left, the room photo carrying the
    stay label and progress bar on the right.
  */
  const room = booking?.rooms;
  const property = room?.properties;
  const address =
    [property?.address, property?.zip_code, property?.city]
      .filter((part) => String(part ?? "").trim())
      .join(", ") ||
    (property?.title ?? "");
  const floor = String(room?.floor ?? "").trim();
  const floorLine = floor ? d.floorLine.replace("{floor}", floor) : null;
  const photoRaw = firstImageUrl(room?.images) ?? property?.image_url ?? null;
  const photo = photoRaw ? getImageUrl(photoRaw, { width: 960 }) : null;

  const day = (iso: unknown) => {
    const raw = String(iso ?? "");
    if (!raw) return null;
    const parsed = parseDateOnly(raw);
    return Number.isNaN(parsed.getTime()) ? null : formatTenantDate(parsed);
  };

  const todayLine = new Date(now).toLocaleDateString(
    localeFor(activeLanguage),
    { weekday: "long", day: "numeric", month: "long", year: "numeric" }
  );

  const stay = stayProgress(booking, now);
  const stayLabel = (() => {
    switch (stay.kind) {
      case "before_move_in":
        return d.stayMoveInOn.replace("{date}", day(stay.moveInIso) ?? "—");
      case "days_until_move_in":
        return d.stayMoveInDays.replace("{days}", String(stay.days));
      case "living":
        return d.stayDayOf
          .replace("{day}", String(stay.day))
          .replace("{total}", String(stay.total));
      case "ending":
        return d.stayDaysLeft.replace("{days}", String(stay.daysLeft));
      default: {
        /*
          No confirmed move-out, so there is no total to count toward — most
          tenancies, since `end_date` is only the earliest possible move-out
          and contracts roll over. The move-in date is still a true thing to
          say, and the bar stays hidden rather than being drawn against a
          date nobody confirmed.
        */
        const moveIn = day(booking?.move_in_date ?? booking?.start_date);
        return moveIn ? d.stayMoveInOn.replace("{date}", moveIn) : null;
      }
    }
  })();

  const heroLine = (() => {
    switch (stay.kind) {
      case "before_move_in":
        return d.heroFromDate.replace("{date}", day(stay.moveInIso) ?? "—");
      case "days_until_move_in":
        return d.heroMoveInDays.replace("{days}", String(stay.days));
      case "ending":
        return d.heroEnding
          .replace("{days}", String(stay.daysLeft))
          .replace("{property}", property?.title ?? "");
      default:
        // Living with nothing outstanding needs no sub-line — the design
        // leaves it blank rather than inventing reassurance.
        return null;
    }
  })();

  const journeyIndex = heroJourneyIndex(stage as any);
  const journeyVisible = showHeroJourney(stage as any);
  const journeyLabel: Record<string, string> = {
    application: d.journeyApplication,
    contract: d.journeyContract,
    deposit: d.journeyDeposit,
    moveIn: d.journeyMoveIn,
    movedIn: d.journeyMovedIn,
  };

  /*
    The design's phase card: a sentence, a body, and its OWN call to action.

    The export gives each phase its own button — "Review contract", "Go to
    payments", "Prepare move-in", "Book appointment" — where this had one
    generic "Take care of it" for every state. The ranking underneath is
    unchanged; only what the card says about it is.
  */
  const money_ = (value: unknown) => {
    const n = Number(value);
    return Number.isFinite(n)
      ? new Intl.NumberFormat(localeFor(activeLanguage), {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        }).format(n)
      : "";
  };
  const moveInDay = day(booking?.move_in_date ?? booking?.start_date) ?? "";
  const moveOutDay =
    day(booking?.actual_move_out_date ?? booking?.move_out_date) ?? "";
  const depositDue = money_(money.depositRemainingCents / 100);

  /*
    Is the handover form actually open, or merely coming up?

    `computeProtocolActionNeeded` already draws this line -- "upcoming" is the
    day-before nag for a window that opens on the move-in day itself, "start"
    and "resume" mean the tenant can fill it in right now -- but the overview
    only ever read the KIND. So a tenant whose move-in was months ago and
    whose protocol is still open was told "You move in on 19.05.2026", a date
    in the past, under a button called "Prepare move-in". Neel caught it on
    prod (23 Oct); the fix is to pass the reason through, not to re-derive it.
  */
  const protocolOpenNow =
    !protocolStateUnknown &&
    !!protocolAction &&
    protocolAction.reason !== "upcoming";

  const titleFor = (kind: string): string =>
    (
      ({
        sign: d.actionSign,
        deposit: d.actionDeposit,
        overdue: d.actionOverdue,
        move_in: protocolOpenNow
          ? d.actionMoveInReport
          : d.actionMoveIn.replace("{date}", moveInDay),
        key_return: d.actionKeyReturn,
        /*
          Same card as `move_in`, deliberately. "Du ziehst am 1. September
          ein" + "Einzug vorbereiten" is the right thing to say both when the
          protocol is open and when move-in is simply ahead -- the difference
          is only WHEN it surfaces, which the ranking decides.
        */
        move_in_upcoming: d.actionMoveIn.replace("{date}", moveInDay),
        move_out: d.actionMoveOut.replace("{date}", moveOutDay),
        support: d.actionSupport,
        referral_reward: d.actionReferralReward,
      }) as Record<string, string>
    )[kind] ?? "";

  const bodyFor = (kind: string): string =>
    (
      ({
        sign: d.bodySign,
        deposit: d.bodyDeposit.replace("{amount}", depositDue),
        overdue: d.bodyOverdue,
        move_in: protocolOpenNow ? d.bodyMoveInReport : d.bodyMoveIn,
        move_in_upcoming: d.bodyMoveIn,
        /*
          Within the week before the move-out the card stops describing the
          appointment and names the consequence instead -- the protocol is
          what stands between the tenant and a deduction from their deposit.
          `computeProtocolActionNeeded` arms move-out exactly seven days out
          (`MOVE_OUT_ALERT_LEAD_DAYS`), which is the week Neel asked for.
        */
        move_out: protocolOpenNow ? d.bodyMoveOutReport : d.bodyMoveOut,
        key_return: d.bodyKeyReturn,
        support: d.bodySupport,
        referral_reward: d.bodyReferralReward,
      }) as Record<string, string>
    )[kind] ?? "";

  const ctaFor = (kind: string): string =>
    (
      ({
        sign: d.ctaSign,
        deposit: d.ctaDeposit,
        overdue: d.ctaOverdue,
        move_in: protocolOpenNow ? d.ctaProtocol : d.ctaMoveIn,
        move_in_upcoming: d.ctaMoveIn,
        move_out: protocolOpenNow ? d.ctaProtocol : d.ctaMoveOut,
        key_return: d.ctaKeyReturn,
        support: d.ctaSupport,
        referral_reward: d.ctaReferralReward,
      }) as Record<string, string>
    )[kind] ?? d.openAction;

  const Hero = ({ children }: { children?: React.ReactNode }) => (
    <section className="space-y-7">
      <div className="grid gap-8 lg:grid-cols-2 lg:items-stretch">
        <div className="flex min-w-0 flex-col justify-center gap-7 py-3">
          <div className="flex flex-col gap-3">
            <span className="text-sm text-muted-foreground">{todayLine}</span>
            <h1 className="text-4xl font-semibold leading-[1.03] tracking-[-0.045em] text-foreground sm:text-5xl lg:text-[3.5rem]">
              {greeting}
            </h1>
            {heroLine ? (
              <p className="max-w-[480px] text-[17px] leading-[1.55] text-muted-foreground">
                {heroLine}
              </p>
            ) : null}
          </div>
          {children}
          {/*
            The design shows these only once the tenancy is live — before that
            Community and Empfehlungen are locked, so two of the three would be
            dead buttons.
          */}
          {!journeyVisible ? (
            <div className="flex flex-wrap gap-2.5">
              <Button className="rounded-full" onClick={() => onOpen("help")}>
                {d.quickReportIssue}
              </Button>
              {availableTabs.includes("referrals") ? (
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => onOpen("referrals")}
                >
                  {d.quickReferrals}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        {/*
          The room photo. `aria-hidden` on the image and the facts repeated as
          text underneath would be duplication, so the overlay IS the text —
          it sits in the DOM, not baked into the picture.
        */}
        <div className="relative min-h-[260px] overflow-hidden rounded-[20px] bg-muted sm:min-h-[340px] lg:min-h-[440px]">
          {photo ? (
            <img
              src={photo}
              alt=""
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : null}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[45%] bg-gradient-to-b from-transparent to-[rgba(6,32,31,0.55)]" />
          <div className="pointer-events-none absolute inset-x-7 bottom-6 flex flex-col gap-3">
            <div className="flex items-end justify-between gap-4">
              <div className="flex min-w-0 flex-col gap-1">
                <span className="truncate text-2xl font-semibold leading-[1.1] tracking-[-0.03em] text-white">
                  {room?.title ?? property?.title ?? ""}
                </span>
                {floorLine ? (
                  <span className="truncate text-[15px] text-white/90">
                    {floorLine}
                  </span>
                ) : null}
                <span className="truncate text-[15px] text-white/90">
                  {address}
                </span>
              </div>
              {stayLabel ? (
                <span className="shrink-0 text-[15px] font-semibold text-white">
                  {stayLabel}
                </span>
              ) : null}
            </div>
            {stay.percent !== null ? (
              <div
                className="h-1 overflow-hidden rounded-full bg-white/30"
                role="progressbar"
                aria-valuenow={stay.percent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={stayLabel ?? undefined}
              >
                <div
                  className="h-full rounded-full bg-white"
                  style={{ width: `${stay.percent}%` }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/*
        The Wohnen tiles. The design shows three — Next rent, Quick look and
        Your requests.

        Quick look is STILL left out: it is Wi-Fi and the broadcasting-fee
        number, and neither exists as a column anywhere, so it would render
        empty. That needs a schema decision, not markup (Vishnu, 14 Oct:
        leave it).

        Your requests is now built — the issues read it needed arrived with
        the Hilfe port, and this tile reuses it rather than adding a second.
      */}
      {!journeyVisible ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {/*
            Next rent needs its own figure to show; Your requests does not,
            so it used to be gated on `money.nextRent` too -- a tenant with
            nothing currently due (paid ahead, or between charges) lost the
            requests tile along with it, for a reason that has nothing to do
            with requests.
          */}
          {money.nextRent ? (
            <Card className="rounded-[20px] border-0 bg-muted/60 p-6">
              <p className="text-sm text-muted-foreground">{d.tileNextRent}</p>
              {/*
                The REMAINING balance, via the same helper the payments screen
                uses. Showing `amount_cents` meant a part-paid row read €650
                here and €350 there — two screens disagreeing about one charge.
              */}
              <p className="mt-2 text-3xl font-semibold tracking-[-0.02em] text-foreground">
                {money_(remainingRentCents(money.nextRent) / 100)}
              </p>
              {day(money.nextRent.due_date) ? (
                <p className="mt-2 text-sm text-muted-foreground">
                  {/*
                    Neutral wording: this tile does not know the effective
                    payment method, and "Debit on" promised a collection that
                    never happens for a bank-transfer or card tenant.
                  */}
                  {d.tileDueOn.replace(
                    "{date}",
                    day(money.nextRent.due_date) ?? ""
                  )}
                </p>
              ) : null}
            </Card>
          ) : null}

          {/*
            Open requests, as a COUNT and the newest title -- not the thread.
            Opening a conversation belongs on Hilfe, which already mounts
            `IssueChatThread`; a second copy here would be two readers of one
            conversation drifting apart. The tile links there instead.
          */}
          <Card className="rounded-[20px] border-0 bg-muted/60 p-6">
            <p className="text-sm text-muted-foreground">{d.tileRequests}</p>
            {/*
              Unresolved is not "none reported". `data = []` is this hook's
              default while pending AND after a failure alike, so reading it
              unconditionally told a tenant with real open requests "Nothing
              reported" for as long as the query was in flight, or forever on
              an error -- the same loading/error trap the rest of this
              component already guards against for money and the protocol
              state.
            */}
            {issuesPending || issuesFailed ? (
              <p className="mt-2 text-sm text-muted-foreground">
                {issuesFailed ? d.loadFailed : d.loading}
              </p>
            ) : openIssues.length > 0 ? (
              <>
                <p className="mt-2 text-3xl font-semibold tracking-[-0.02em] text-foreground">
                  {openIssues.length}{" "}
                  <span className="text-base font-medium text-muted-foreground">
                    {d.tileRequestsOpen}
                  </span>
                </p>
                <p className="mt-2 truncate text-sm text-muted-foreground">
                  {String(openIssues[0]?.title ?? "")}
                </p>
              </>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">
                {d.tileRequestsEmpty}
              </p>
            )}
            {availableTabs.includes("help") ? (
              <button
                type="button"
                onClick={() => onOpen("help")}
                className="mt-3 text-sm font-semibold text-foreground underline underline-offset-4"
              >
                {d.tileRequestsMore}
              </button>
            ) : null}
          </Card>
        </div>
      ) : null}

      {journeyVisible ? (
        <ol className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {HERO_JOURNEY_STEPS.map((step, i) => {
            const done = i < journeyIndex;
            const current = i === journeyIndex;
            return (
              <li
                key={step}
                aria-current={current ? "step" : undefined}
                className={[
                  "min-w-0 pt-3.5",
                  current
                    ? "border-t-[3px] border-foreground"
                    : done
                      ? "border-t-[1.5px] border-foreground"
                      : "border-t-[1.5px] border-border opacity-55",
                ].join(" ")}
              >
                <div className="flex items-center justify-between gap-2">
                  <span
                    className={[
                      "truncate text-[15px] tracking-[-0.01em] text-foreground",
                      current ? "font-semibold" : "font-medium",
                    ].join(" ")}
                  >
                    {journeyLabel[step]}
                  </span>
                  {done ? <Check className="h-4 w-4 shrink-0" /> : null}
                </div>
              </li>
            );
          })}
        </ol>
      ) : null}
    </section>
  );

  /*
    Nothing outstanding — or nothing we can be sure about.

    Restored deliberately after the hero port: the protocol half of "nothing
    to do" is only trustworthy once its own booking actually resolved, and a
    failed query must not read as "all handled". Both states render inside the
    hero now, so the room photo and journey strip stay put either way.
  */
  if (!top) {
    /*
      The design has no card at all once the tenancy is live — the quick
      actions and the tiles below carry the screen. Before that, an applicant
      with nothing outstanding gets "We're reviewing your application", which
      is the export's Bewerbung state and the honest thing to say while an
      admin has it.
    */
    const applied = journeyIndex === 0;
    return (
      <Hero>
        <Card className="rounded-[20px] p-6">
          {protocolStateUnknown ? (
            <p className="text-destructive">{d.loadFailed}</p>
          ) : applied ? (
            <>
              <p className="text-sm text-muted-foreground">
                {d.appliedEyebrow}
              </p>
              <h2 className="mt-1 text-[22px] font-semibold leading-[1.2] tracking-[-0.03em] text-foreground">
                {d.appliedTitle}
              </h2>
              <p className="mt-2 text-[15px] leading-[1.5] text-muted-foreground">
                {d.appliedBody}
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {d.allDoneEyebrow}
              </p>
              <h2 className="mt-1 text-[22px] font-semibold leading-[1.2] tracking-[-0.03em] text-foreground">
                {d.allDoneTitle}
              </h2>
              <p className="mt-2 text-[15px] leading-[1.5] text-muted-foreground">
                {d.allDoneBody}
              </p>
            </>
          )}
        </Card>
      </Hero>
    );
  }

  return (
    <Hero>
      <Card className="rounded-[20px] bg-foreground p-6 text-background sm:px-[26px]">
        <p className="text-sm text-background/70">{d.nextStepEyebrow}</p>
        <h2 className="mt-1.5 text-[22px] font-semibold leading-[1.2] tracking-[-0.03em]">
          {titleFor(top.kind)}
        </h2>
        <p className="mt-1.5 text-[15px] leading-[1.5] text-background/80">
          {bodyFor(top.kind)}
        </p>
        <Button
          variant="secondary"
          className="mt-4 h-[42px] rounded-full px-[18px]"
          onClick={() => onOpen(tabFor(top.tab))}
        >
          {ctaFor(top.kind)}
          <ArrowRight className="ml-2 h-[15px] w-[15px]" />
        </Button>
      </Card>
    </Hero>
  );
}
